// Deep path, not the `/types` barrel. That barrel is CommonJS re-exporting 68 modules, so
// webpack cannot tree-shake it — pulling two constants through it costs ~20 kB gzipped in
// the bundle. This file is a standalone enum with no imports of its own.

export const UserPreferenceTab = {
    CLIENTS: 'clients',
    STAFF: 'staff',
    SCHEDULE: 'schedule',
    PAYERS: 'payers',
    TASKS: 'tasks',
    BILLING_OVERVIEW: 'billingOverview',
    CLAIMS: 'claims',
    INVOICES: 'invoices',
    CLAIM_PAYMENTS: 'claimPayments',
}

export const USER_PREFERENCE_FILTERS_MAX_BYTES = 8 * 1024;

const TAB_BY_PATHNAME = {
    '/client': UserPreferenceTab.CLIENTS,
    '/staff': UserPreferenceTab.STAFF,
    '/schedule': UserPreferenceTab.SCHEDULE,
    '/fundingSource': UserPreferenceTab.PAYERS,
    '/tasks': UserPreferenceTab.TASKS,
    '/overview': UserPreferenceTab.BILLING_OVERVIEW,
    '/claims': UserPreferenceTab.CLAIMS,
    '/invoices': UserPreferenceTab.INVOICES,
    '/claimPayments': UserPreferenceTab.CLAIM_PAYMENTS,
};

// Paging, free-text search and the selected sub-tab are per-visit — only filters are remembered.
// Every grid's search box writes the `searchKey`/`searchValue` pair (tabs/searchAndFilerFull,
// menuBar searchInput); `tabType` is buttons/tabSwitcher, `activeTab` is tabs/SimpleTabs.
const TRANSIENT_KEYS = ['page', 'skip', 'limit', 'searchKey', 'searchValue', 'tabType', 'activeTab'];

/**
 * The sub-tab a grid opens on, for grids split by a `TabSwitcher`.
 *
 * Switching sub-tab resets the filters, so a filter set belongs to one sub-tab. Since the
 * sub-tab is not saved, the grid always reopens on its default — so only filters set on the
 * default sub-tab are saved. Saving another sub-tab's would restore them onto the wrong grid
 * (Submitted-claim filters over Billable appointments).
 *
 * Lazily required for the same reason as `store` below: these constants import `utils`.
 */
const defaultSubTab = (tab) => {
    if (tab === UserPreferenceTab.CLAIMS) return require('pages/billing/claim/constants').claimTabEnums.BILLABLE;
    if (tab === UserPreferenceTab.INVOICES) return require('pages/billing/invoices/constants').invoiceTabEnums.INVOICEABLE_APPT;
    return undefined;
};

/** True when `state` is a non-default sub-tab, whose filters must be neither saved nor restored. */
const isOffDefaultSubTab = (tab, state) => {
    const fallback = defaultSubTab(tab);
    return !!fallback && !!state?.tabType && state.tabType !== fallback;
};

const TRANSIENT_KEYS_BY_TAB = {
    [UserPreferenceTab.SCHEDULE]: ['firstDate', 'lastDate'],
};

const DEBOUNCE_MS = 600;

/** Pending write per tab, so a burst of filter changes costs one request. */
const pendingSaves = {};

/**
 * State a grid is handed through sessionStorage instead of through the router, and which
 * therefore outranks anything saved.
 *
 * Only the schedule has any, and both of its entries load `/schedule` cold with no router
 * state: "View Calendar" (`calendarState`) opens a new tab already scoped to one staff or
 * client, and the date-window picker (`apptDates`) survives navigating away and back.
 * Without this the saved filters would be restored *over* the thing the user just asked
 * to see — the staff they clicked would vanish behind their own default filters.
 *
 * Layered in the order fragments/schedule/scheduleFragment.js layers them. That file still
 * owns reading and clearing these; this only keeps the restore from clobbering them first.
 */
const HANDOFF_KEYS_BY_TAB = {
    [UserPreferenceTab.SCHEDULE]: ['calendarState', 'apptDates'],
};

const PERSON_SCOPE_KEYS = ['staffs', 'staffFull', 'clients', 'clientFull', 'ownStaff', 'staff', 'client'];

/** The grid whose filters this pathname owns, or undefined if it owns none. */
export const gridFilterTab = (pathname) => TAB_BY_PATHNAME[pathname];

const readHandoff = (key) => {
    try {
        return JSON.parse(sessionStorage.getItem(key)) || {};
    } catch (e) {
        return {}; // Corrupt entry — scheduleFragment ignores it too.
    }
};

const withoutPersonScope = (state) => {
    const next = {...state};
    PERSON_SCOPE_KEYS.forEach((key) => delete next[key]);
    return next;
};

/**
 * What to seed a grid's `location.state` with when the router carries none.
 *
 * The saved copy is sanitized again because rows written before a key became transient
 * (search, the sub-tab) still carry it; the next filter change rewrites the row without it.
 * A legacy row saved on a non-default sub-tab is dropped whole — see `defaultSubTab`.
 * Handoffs are not sanitized — `apptDates` legitimately carries the schedule's date window.
 */
export const restoredGridState = (tab, savedFilters) =>
    (HANDOFF_KEYS_BY_TAB[tab] || []).reduce(
        (state, key) => {
            const handoff = readHandoff(key);
            const scopesPerson = handoff.staffs?.length || handoff.clients?.length;
            return {...(scopesPerson ? withoutPersonScope(state) : state), ...handoff};
        },
        isOffDefaultSubTab(tab, savedFilters) ? {} : sanitizeFilters(tab, savedFilters),
    );

/**
 * Key-ordered JSON, so an equality check survives a round trip through Mongo.
 *
 * What comes back from the bootstrap read has whatever key order it was stored in;
 * what we build here has whatever order `location.state` was spread in. Plain
 * `JSON.stringify` would call those different and write on every navigation.
 */
const stableStringify = (value) => {
    if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
    const entries = Object.keys(value)
        .sort()
        .filter((key) => value[key] !== undefined)
        .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`);
    return `{${entries.join(',')}}`;
};

const sanitizeFilters = (tab, state) => {
    const filters = {...(state || {})};
    [...TRANSIENT_KEYS, ...(TRANSIENT_KEYS_BY_TAB[tab] || [])].forEach((key) => delete filters[key]);
    // An explicitly cleared filter is stored as absent, not as null — otherwise the
    // restored state carries dead keys that some grids treat as "filtered by nothing".
    Object.keys(filters).forEach((key) => {
        if (filters[key] === undefined || filters[key] === null) delete filters[key];
    });
    return filters;
};

const byteLength = (text) => new TextEncoder().encode(text).length;

/**
 * Mirror a grid's filter state to the user's preferences.
 *
 * Called from `SaveParams`/`SavePage` with the state they just pushed onto the router.
 * A no-op for any path that does not own a tab, which is most of them.
 */
export const persistGridFilters = (pathname, state) => {
    const tab = gridFilterTab(pathname);
    if (!tab) return;
    // Leave the default sub-tab's saved filters in place; this sub-tab's are per-visit.
    if (isOffDefaultSubTab(tab, state)) return;

    // Lazily required: the store imports `utils`, so importing it at the top of this
    // file would close that cycle at module-evaluation time. Same idiom as `utils/index`.
    const {store, userPreferencesActions} = require('store');

    const preferences = store.getState()?.userPreferences;
    // Before the bootstrap read settles we have nothing to compare against, and the
    // restore gate has not run yet either — writing now could persist the unfiltered
    // state of a grid that is about to be filtered from the server's own copy.
    if (!preferences?.loaded) return;

    const filters = sanitizeFilters(tab, state);
    const serialized = stableStringify(filters);
    if (serialized === stableStringify(preferences.filters?.[tab] || {})) return;

    // Keep the store ahead of the request: the gate reads it on the next navigation,
    // which can happen well inside the debounce window.
    store.dispatch(userPreferencesActions.setTabFilters(tab, filters));

    // The API answers 413 over the cap. Skipping the write leaves the older, smaller
    // server copy in place — this session still behaves correctly off the store, and
    // the next one restores slightly less rather than nothing.
    if (byteLength(serialized) > USER_PREFERENCE_FILTERS_MAX_BYTES) return;

    clearTimeout(pendingSaves[tab]);
    pendingSaves[tab] = setTimeout(() => {
        delete pendingSaves[tab];
        const isEmpty = Object.keys(filters).length === 0;
        store.dispatch(isEmpty ? userPreferencesActions.clearTabFilters(tab) : userPreferencesActions.saveTabFilters(tab, filters));
    }, DEBOUNCE_MS);
};

/** Drop debounced writes that have not fired. Called on logout, before the token goes. */
export const cancelPendingGridFilterSaves = () => {
    Object.keys(pendingSaves).forEach((tab) => {
        clearTimeout(pendingSaves[tab]);
        delete pendingSaves[tab];
    });
};
